/**
 * Minimal, privacy-safe marketing event helper.
 *
 * This is intentionally NOT an event framework. It forwards a tiny, fixed set
 * of anonymous interaction events to whichever privacy-first analytics
 * provider is loaded (Plausible / Umami), and is a no-op everywhere else.
 *
 * Hard rules (mirrored by `scripts/smoke-analytics.ts`):
 *   - Only the named events below may be sent — no free-form event names.
 *   - NO personal data, ever: no user email/name, no issue keys, no tenant
 *     ids, no query strings, no request/response bodies. Events carry their
 *     name and nothing else.
 *   - Nothing is sent unless analytics is actually allowed to run on this host
 *     (production + indexable host), so dev/staging/preview/localhost stay
 *     silent even if a button is wired up.
 *
 * Page-views are handled by the provider script itself; these CTA events are
 * optional sugar for existing buttons and are safe to leave unused.
 */
import { shouldLoadAnalytics } from "@/lib/analytics";

/**
 * Video interaction events. Still a fixed, finite set: the group names come
 * from the video manifest (`marketing-videos.ts` analyticsName) and the verbs
 * are enumerated here, so every possible event name is known at compile time.
 * Events carry NO metadata — the video id, page and variant are encoded in the
 * name itself; playback position, captions state etc. are never sent beyond
 * the coarse progress verbs below.
 */
export type VideoEventGroup = "overview" | "support" | "engineering" | "hr";
export type VideoEventVerb =
  | "impression"
  | "play"
  | "pause"
  | "progress_25"
  | "progress_50"
  | "progress_75"
  | "complete"
  | "captions_on"
  | "modal_open"
  | "modal_close"
  | "cta";

export type MarketingEvent =
  | "demo_cta_clicked"
  | "contact_email_clicked"
  | "signup_clicked"
  | "docs_link_clicked"
  | `video_${VideoEventGroup}_${VideoEventVerb}`;

// Provider globals injected by the analytics scripts. Typed narrowly so we
// never pass anything but the bare event name.
declare global {
  interface Window {
    plausible?: (event: string) => void;
    umami?: { track?: (event: string) => void };
  }
}

/**
 * Fire an anonymous marketing event. Silently does nothing when analytics is
 * not allowed on this host or no provider is loaded. Never throws.
 */
export function track(event: MarketingEvent): void {
  if (typeof window === "undefined") return;
  if (!shouldLoadAnalytics(window.location.host)) return;

  try {
    if (typeof window.plausible === "function") {
      window.plausible(event);
      return;
    }
    if (window.umami && typeof window.umami.track === "function") {
      window.umami.track(event);
    }
  } catch {
    // Analytics must never break the UI. Swallow any provider error.
  }
}

export {};
